"use client";

import { useState } from "react";

import { useAuth } from "@/features/auth";
import { initials } from "@/lib/string/initials";
import { cn } from "@/lib/utils";

interface CandidateAvatarProps {
  /** Display name — used to derive the two-letter initials fallback. */
  name: string | null | undefined;
  /**
   * Public CDN avatar URL from `/api/profiles/{id}` or the pipeline list
   * (`https://avatars.arbi.ai/{linkedin_username}`). When null/undefined,
   * renders initials immediately (no network request, no fade).
   */
  avatarUrl?: string | null;
  /** Outer circle diameter in px. */
  size: number;
  /**
   * Initials text size in px. Defaults to a sensible 45% of `size`; pass
   * an override if the design calls for tighter type.
   */
  initialsPx?: number;
  className?: string;
}

/**
 * Avatar with graceful, flash-free fallback semantics.
 *
 * State machine:
 *   - No `avatarUrl` given       → initials rendered immediately at opacity 1.
 *   - URL given, image loading   → dark circle only (no initials peek-through).
 *   - URL given, image loaded    → image fades in from 0→1 over 200ms.
 *   - URL given, image 404s      → initials fade in from 0→1 over 200ms.
 *
 * The user NEVER sees both initials and the photo at the same time, so
 * there's no "flash of wrong content" when the image request resolves.
 *
 * A refetch that returns the same avatar URL does NOT reset the state —
 * we diff by `pathname` so the cached image stays mounted through polling.
 * Only a different path triggers a fresh load.
 */
export function CandidateAvatar({
  name,
  avatarUrl,
  size,
  initialsPx,
  className,
}: CandidateAvatarProps) {
  type Status = "loading" | "loaded" | "errored";
  const { complianceSettings } = useAuth();
  const photosEnabled = complianceSettings?.show_candidate_photos !== false;
  const effectiveAvatarUrl = photosEnabled ? avatarUrl : null;
  const currentPath = toPath(effectiveAvatarUrl ?? null);

  const [imageState, setImageState] = useState<{
    path: string | null;
    status: Status;
  }>(() => ({
    path: currentPath,
    status: currentPath ? "loading" : "errored",
  }));
  const status =
    imageState.path === currentPath
      ? imageState.status
      : currentPath
        ? "loading"
        : "errored";

  const displayInitials = initials(name || "?") || "?";
  const initialsFontPx = initialsPx ?? Math.max(9, Math.round(size * 0.45));

  // The presence of the `<img>` tag in the tree is gated on having a URL
  // to actually try — otherwise the element triggers onerror with src="".
  const shouldAttemptImage = !!effectiveAvatarUrl && status !== "errored";
  const showInitials = status === "errored";
  const showImage = status === "loaded" && !!effectiveAvatarUrl;

  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#0D111A] shadow-[0px_0.55px_1.1px_0px_rgba(0,0,0,0.05)]",
        className,
      )}
      style={{ width: size, height: size }}
    >
      {/* Initials fade in only when we know there's no photo to display. */}
      <span
        aria-hidden={!showInitials}
        className="font-semibold text-[#2C7EFF] transition-opacity duration-200 ease-out"
        style={{
          fontSize: `${initialsFontPx}px`,
          lineHeight: 1,
          opacity: showInitials ? 1 : 0,
        }}
      >
        {displayInitials}
      </span>

      {shouldAttemptImage && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={effectiveAvatarUrl ?? ""}
          alt=""
          onLoad={() => setImageState({ path: currentPath, status: "loaded" })}
          onError={() => setImageState({ path: currentPath, status: "errored" })}
          draggable={false}
          decoding="async"
          className="absolute inset-0 size-full object-cover transition-opacity duration-200 ease-out"
          style={{ opacity: showImage ? 1 : 0 }}
        />
      )}

      {/* Figma bottom inner-shadow seam. Pointer-events none so wrapping
          buttons still receive clicks. */}
      <div className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-0.55px_0px_0px_rgba(0,0,0,0.08)]" />
    </div>
  );
}

/** Strip the query string so URL variants of the same file are a no-op. */
function toPath(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, "http://localhost");
    return parsed.pathname;
  } catch {
    const q = url.indexOf("?");
    return q === -1 ? url : url.slice(0, q);
  }
}
